import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import rem from 'polished/lib/helpers/rem'
import styled from 'styled-components'
import Label from 'LuxKit/Label'
import TextLink from 'LuxKit/TextLink'
import ReserveForZeroPolicyModalContent from './ReserveForZeroPolicyModalContent'
import switchFunc from 'lib/function/switchFunc'
import { depositAmountStringBuilder } from 'lib/tours/tourUtils'
import DepositInfoModalContent from '../DepositInfo/DepositInfoModalContent'

import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { isOfferDefaultDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { useAppSelector } from 'hooks/reduxHooks'

enum ModalSteps {
  deposit = 'deposit',
  reserve = 'reserve',
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const Section = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(20)};

  &:nth-child(2) {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    width: 50%;

    &:nth-child(2) {
      margin-top: 0;
      margin-left: ${rem(20)};
    }
  }
`

const SectionSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(20)};
`

const TopSection = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
`

const BottomSection = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  margin-top: ${rem(16)};
`

interface Props {
  offerType: App.OfferType
  depositAmountPercentage: number
  depositType?: App.Tours.V2OfferDepositType
  depositThresholds?: App.DepositThreshold
  depositAmount?: number
}

function ReserveForZeroAndDepositPolicyModal({
  depositAmountPercentage,
  depositType,
  depositThresholds,
  offerType,
  depositAmount,
}: Props) {
  const intl = useIntl()
  const [step, setStep] = useState<ModalSteps>()

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const depositShortLabel = !depositType ? undefined : depositAmountStringBuilder(defaultDepositAmountPercentage, depositType, depositAmountPercentage, intl).short

  const depositTitleString = isOfferUsingDefaultDepositPercentage
    ? intl.formatMessage({
        id: 'eIjBQBWDJP',
        defaultMessage: 'a deposit',
        description: 'Generic deposit label used in modal title',
      })
    : !depositType
        ? intl.formatMessage(
            {
              id: 'JmPdb2pIBu',
              defaultMessage: '{percentage}% deposit',
              description: 'Percentage-based deposit label for modal title',
            },
            { percentage: depositAmountPercentage },
          )
        : intl.formatMessage(
            {
              id: 'UDFh+muqRX',
              defaultMessage: '{depositLabel} deposit',
              description: 'Deposit label with amount for modal title',
            },
            { depositLabel: depositShortLabel },
          )

  const depositAmountString = isOfferUsingDefaultDepositPercentage
    ? intl.formatMessage({
        id: '8U7nW2tyQi',
        defaultMessage: 'deposit',
        description: 'Generic deposit label used in deposit amount context',
      })
    : !depositType
        ? intl.formatMessage(
            {
              id: 'pgZZpt9ZvH',
              defaultMessage: '{percentage}% deposit',
              description: 'Percentage-based deposit label for deposit amount context',
            },
            { percentage: depositAmountPercentage },
          )
        : intl.formatMessage(
            {
              id: 'XIj5fatdqy',
              defaultMessage: '{depositLabel} deposit',
              description: 'Deposit label with amount for deposit amount context',
            },
            { depositLabel: depositShortLabel },
          )

  const title = switchFunc({
    deposit: intl.formatMessage({
      id: 'D4SC4Ov2Ka',
      defaultMessage: 'Pay {depositTitleString}',
      description: 'Modal title for pay deposit step',
    }, { depositTitleString }),
    reserve: intl.formatMessage({
      id: 'z0zCNXmjif',
      defaultMessage: 'Reserve now for free',
      description: 'Modal title for reserve for free step',
    }),
  }, intl.formatMessage({
    id: '2fitN5EhRc',
    defaultMessage: 'Flexible payment options',
    description: 'Modal title for flexible payment options overview',
  }))(step)

  const subtitle = switchFunc({
    deposit: undefined,
    reserve: undefined,
  }, intl.formatMessage({
    id: 'CA71jWk3I7',
    defaultMessage: 'Book with ease by choosing one of our flexible payment methods below.',
    description: 'Modal subtitle for flexible payment options overview',
  }))(step as string)

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      size="L"
      onBack={step ? () => setStep(undefined) : undefined}
    >
      {step === ModalSteps.reserve && (
        <ReserveForZeroPolicyModalContent />
      )}
      {step === ModalSteps.deposit && (
        <DepositInfoModalContent
          offerType={offerType}
          depositAmountPercentage={depositAmountPercentage}
          depositThresholds={depositThresholds}
          depositAmount={depositAmount}
        />
      )}
      {!step && (
        <SectionContainer>
          <Section>
            <TopSection>
              <Heading variant="heading6">
                <FormattedMessage
                  id="tooJZ3V8l8"
                  defaultMessage="Reserve now for free"
                  description="Section heading for reserve for free option"
                />
              </Heading>
              <BodyText variant="medium">
                <FormattedMessage
                  id="OohcUcDPLL"
                  defaultMessage="We’ll hold your reservation for 48 hours so you have time to think it over."
                  description="Body text explaining 48 hour reservation hold"
                />
              </BodyText>
              <div>
                <Label kind="tertiary" variant="warning">
                  <FormattedMessage
                    id="1pYcZoSjl5"
                    defaultMessage="Limit of 2 reservations per member"
                    description="Warning label about reservation limit"
                  />
                </Label>
              </div>
            </TopSection>
            <BottomSection>
              <TextLink onClick={() => setStep(ModalSteps.reserve)} size="large">
                <FormattedMessage
                  id="AVDd/4Cw6f"
                  defaultMessage="Learn more"
                  description="Link text to learn more about reserve for free"
                />
              </TextLink>
            </BottomSection>
          </Section>
          <SectionSeparator>
            <Heading variant="heading6">
              <FormattedMessage
                id="ucwmB8ul9w"
                defaultMessage="Or"
                description="Separator between reserve and deposit options"
              />
            </Heading>
          </SectionSeparator>
          <Section>
            <TopSection>
              <Heading variant="heading6">
                <FormattedMessage
                  id="6nyhxeGxF1"
                  defaultMessage="Pay {depositTitleString} or in full today"
                  description="Section heading for pay deposit or full amount option"
                  values={{ depositTitleString }}
                />
              </Heading>
              <BodyText variant="medium">
                <FormattedMessage
                  id="DegLqpz6z8"
                  defaultMessage="Secure your dates by paying the full amount upfront or with a {depositAmountString} of your accommodation booking."
                  description="Body text explaining deposit payment option"
                  values={{ depositAmountString }}
                />
              </BodyText>
            </TopSection>
            <BottomSection>
              <TextLink onClick={() => setStep(ModalSteps.deposit)} size="large">
                <FormattedMessage
                  id="J/I8DGqRWd"
                  defaultMessage="Learn more"
                  description="Link text to learn more about deposit option"
                />
              </TextLink>
            </BottomSection>
          </Section>
        </SectionContainer>
      )}
    </Modal>
  )
}

export default ReserveForZeroAndDepositPolicyModal
